/**
 * IntelligentResponseCard - Bentley-level premium answer display
 *
 * This component renders SOMA's intelligent responses as a separate,
 * premium-styled card distinct from the process/thinking log.
 *
 * Design Philosophy:
 * - Luxury automotive-inspired aesthetics (Bentley/Rolls-Royce quality)
 * - Glass-morphism with subtle gradients
 * - Confidence visualization with elegant progress indicators
 * - Smooth micro-interactions and animations
 * - Clear separation of answer from process
 *
 * The Answer should feel like receiving a thoughtful response from
 * a world-class advisor - polished, confident, and complete.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    AlertTriangle,
    Sparkles,
    CheckCircle2,
    HelpCircle,
    ChevronDown,
    ChevronUp,
    Copy,
    Share2,
    BookOpen,
    Brain,
    Shield,
    Zap,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Execution verdict from SOMA spine.
 * This is the source of truth for rendering success/failure state.
 */
export type ExecutionVerdict =
    | 'SUCCESS'
    | 'FAILED'
    | 'INCOMPLETE'
    | 'BLOCKED'
    | 'DENIED'
    | 'NOT_APPLICABLE';

/**
 * Content format for deterministic rendering.
 * - plain: Render as plain text (no markdown parsing)
 * - markdown: Render via markdown renderer (bold, bullets, headers, etc.)
 */
export type ContentFormat = 'plain' | 'markdown';

export interface IntelligentResponseData {
    /** The main answer content */
    answer: string;
    /**
     * Content format for deterministic rendering.
     * - plain: Render as plain text (default)
     * - markdown: Render via markdown renderer
     *
     * Backend sets this explicitly based on response type.
     * UI renders based on this field only - no content-based inference.
     */
    contentFormat?: ContentFormat;
    /** Confidence score 0-1 */
    confidence?: number;
    /** Model used for generation */
    model?: string;
    /** Trace ID for provenance */
    traceId?: string;
    /**
     * Execution verdict from SOMA spine.
     * This determines rendering: SUCCESS with artifacts = Verified, FAILED = Error, etc.
     * Takes precedence over legacy `verified` boolean.
     */
    verdict?: ExecutionVerdict;
    /**
     * Reason code for failures/blocks.
     * Displayed when verdict is FAILED or INCOMPLETE.
     */
    reasonCode?: string;
    /**
     * Whether verification artifacts exist.
     * Only show "Verified" badge if verdict=SUCCESS AND hasVerificationArtifacts=true.
     */
    hasVerificationArtifacts?: boolean;
    /** @deprecated Use verdict instead. Legacy verification status */
    verified?: boolean;
    /** Verifier name if verified */
    verifier?: string;
    /** Verification score if available */
    verificationScore?: number;
    /** Time taken to generate response */
    responseTimeMs?: number;
    /** Artifact pointers for provenance */
    artifacts?: {
        plan?: string;
        trace?: string;
        verification?: string;
        ledger?: string;
    };
    /** Next actions suggested by the system */
    nextActions?: Array<{
        label: string;
        type: 'button' | 'link' | 'suggestion';
        payload: string;
    }>;
    /** Sources or references used */
    sources?: Array<{
        title: string;
        url?: string;
        relevance?: number;
    }>;
    /** Key reasoning steps (high-level summary) */
    reasoningSummary?: string[];
    /** Domain/intent classification */
    intentName?: string;
    /** Timestamp */
    timestamp?: Date;
}

interface IntelligentResponseCardProps {
    data: IntelligentResponseData;
    className?: string;
    showProvenance?: boolean;
}

const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 0.85) return 'text-emerald-400';
    if (confidence >= 0.7) return 'text-green-400';
    if (confidence >= 0.5) return 'text-yellow-400';
    return 'text-orange-400';
};

const _getConfidenceGradient = (confidence: number): string => {
    if (confidence >= 0.85) return 'from-emerald-500 to-emerald-400';
    if (confidence >= 0.7) return 'from-green-500 to-green-400';
    if (confidence >= 0.5) return 'from-yellow-500 to-yellow-400';
    return 'from-orange-500 to-orange-400';
};

const getConfidenceLabel = (confidence: number): string => {
    if (confidence >= 0.9) return 'High Confidence';
    if (confidence >= 0.75) return 'Confident';
    if (confidence >= 0.6) return 'Moderate Confidence';
    return 'Lower Confidence';
};

// Threshold for when to show expand/collapse (characters)
const CONTENT_COLLAPSE_THRESHOLD = 400;
// Max height in pixels when collapsed
const COLLAPSED_MAX_HEIGHT = 200;

export const IntelligentResponseCard: React.FC<IntelligentResponseCardProps> = ({
    data,
    className = '',
    showProvenance = true,
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(data.answer);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleCopyTrace = async () => {
        if (!data.traceId) return;
        try {
            await navigator.clipboard.writeText(data.traceId);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy trace:', err);
        }
    };

    const isVerified = data.verdict === 'SUCCESS' && data.hasVerificationArtifacts === true;
    const resolvedConfidence = isVerified
        ? (data.verificationScore ?? data.confidence ?? 0.85)
        : undefined;
    const confidenceLabel = isVerified
        ? getConfidenceLabel(resolvedConfidence ?? 0)
        : data.verdict === 'NOT_APPLICABLE'
          ? 'Chat only'
          : 'Unverified';
    const confidenceValue = resolvedConfidence ?? 0;
    const confidenceColor = isVerified ? getConfidenceColor(confidenceValue) : 'text-slate-400';
    const artifactEntries = data.artifacts
        ? Object.entries(data.artifacts).filter(([, value]) => typeof value === 'string')
        : [];
    const hasMetadata =
        data.sources?.length ||
        data.reasoningSummary?.length ||
        data.traceId ||
        artifactEntries.length > 0;
    const isLongContent = data.answer.length > CONTENT_COLLAPSE_THRESHOLD;

    return (
        <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
                duration: 0.4,
                ease: [0.25, 0.46, 0.45, 0.94], // Luxury easing
            }}
            className={`relative overflow-hidden ${className}`}
        >
            {/* Premium Glass Card */}
            <div className="relative bg-gradient-to-br from-slate-900/95 via-slate-800/90 to-slate-900/95 backdrop-blur-2xl border border-slate-700/40 rounded-2xl shadow-2xl shadow-black/40">
                {/* Subtle ambient glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-violet-500/5 rounded-2xl pointer-events-none" />

                {/* Top accent line */}
                <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-blue-400/50 to-transparent" />

                {/* Header Section */}
                <div className="relative px-6 py-4 border-b border-slate-700/30">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {/* SOMA Icon with glow */}
                            <div className="relative">
                                <div className="absolute inset-0 bg-blue-400/20 blur-lg rounded-full" />
                                <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500/20 to-violet-500/20 border border-blue-400/30 flex items-center justify-center">
                                    <Sparkles className="w-5 h-5 text-blue-400" />
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-white">
                                        Intelligent Response
                                    </span>
                                    {/* Verdict-based badge rendering - constitutional compliance */}
                                    {(() => {
                                        // Use verdict if available, fall back to legacy verified boolean
                                        const verdict = data.verdict;
                                        const hasArtifacts = data.hasVerificationArtifacts;

                                        // SUCCESS with verification artifacts = Verified badge
                                        if (verdict === 'SUCCESS' && hasArtifacts) {
                                            return (
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ delay: 0.3, type: 'spring' }}
                                                    className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full"
                                                >
                                                    <Shield className="w-3 h-3 text-emerald-400" />
                                                    <span className="text-[10px] font-medium text-emerald-400">
                                                        Verified
                                                    </span>
                                                </motion.div>
                                            );
                                        }

                                        // SUCCESS without artifacts = Unverified badge
                                        if (verdict === 'SUCCESS' && !hasArtifacts) {
                                            return (
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ delay: 0.3, type: 'spring' }}
                                                    className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10 border border-yellow-500/30 rounded-full"
                                                >
                                                    <Shield className="w-3 h-3 text-yellow-400" />
                                                    <span className="text-[10px] font-medium text-yellow-400">
                                                        Unverified
                                                    </span>
                                                </motion.div>
                                            );
                                        }

                                        // FAILED = Error badge (never verified)
                                        if (verdict === 'FAILED') {
                                            return (
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ delay: 0.3, type: 'spring' }}
                                                    className="flex items-center gap-1 px-2 py-0.5 bg-red-500/10 border border-red-500/30 rounded-full"
                                                >
                                                    <AlertTriangle className="w-3 h-3 text-red-400" />
                                                    <span className="text-[10px] font-medium text-red-400">
                                                        Failed
                                                    </span>
                                                </motion.div>
                                            );
                                        }

                                        // BLOCKED/DENIED = Blocked badge (action required)
                                        if (verdict === 'BLOCKED' || verdict === 'DENIED') {
                                            return (
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ delay: 0.3, type: 'spring' }}
                                                    className="flex items-center gap-1 px-2 py-0.5 bg-orange-500/10 border border-orange-500/30 rounded-full"
                                                >
                                                    <AlertTriangle className="w-3 h-3 text-orange-400" />
                                                    <span className="text-[10px] font-medium text-orange-400">
                                                        Blocked
                                                    </span>
                                                </motion.div>
                                            );
                                        }

                                        // INCOMPLETE = Incomplete badge (action required)
                                        if (verdict === 'INCOMPLETE') {
                                            return (
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ delay: 0.3, type: 'spring' }}
                                                    className="flex items-center gap-1 px-2 py-0.5 bg-orange-500/10 border border-orange-500/30 rounded-full"
                                                >
                                                    <AlertTriangle className="w-3 h-3 text-orange-400" />
                                                    <span className="text-[10px] font-medium text-orange-400">
                                                        Incomplete
                                                    </span>
                                                </motion.div>
                                            );
                                        }

                                        // NOT_APPLICABLE (answer_only mode) = no badge
                                        if (verdict === 'NOT_APPLICABLE') {
                                            return null;
                                        }

                                        // Legacy fallback: only show Verified if explicitly true AND no verdict
                                        // This maintains backward compatibility but will be deprecated
                                        if (!verdict && data.verified) {
                                            return (
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ delay: 0.3, type: 'spring' }}
                                                    className="flex items-center gap-1 px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full"
                                                >
                                                    <Shield className="w-3 h-3 text-emerald-400" />
                                                    <span className="text-[10px] font-medium text-emerald-400">
                                                        Verified
                                                    </span>
                                                </motion.div>
                                            );
                                        }

                                        return null;
                                    })()}
                                </div>
                                {data.intentName && (
                                    <span className="text-[11px] text-slate-500">
                                        {data.intentName}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Confidence Indicator */}
                        <div className="flex items-center gap-3">
                            {/* Confidence badge */}
                            <div className="flex items-center gap-2">
                                <div className="text-right">
                                    <div className={`text-xs font-medium ${confidenceColor}`}>
                                        {isVerified
                                            ? `${Math.round(confidenceValue * 100)}%`
                                            : '--'}
                                    </div>
                                    <div className="text-[10px] text-slate-500">
                                        {confidenceLabel}
                                    </div>
                                </div>
                                <div className="relative w-10 h-10">
                                    {/* Confidence ring */}
                                    <svg className="w-10 h-10 -rotate-90">
                                        <circle
                                            cx="20"
                                            cy="20"
                                            r="16"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="3"
                                            className="text-slate-700"
                                        />
                                        <motion.circle
                                            cx="20"
                                            cy="20"
                                            r="16"
                                            fill="none"
                                            stroke="url(#confidenceGradient)"
                                            strokeWidth="3"
                                            strokeLinecap="round"
                                            initial={{ strokeDasharray: '0 100' }}
                                            animate={{
                                                strokeDasharray: `${confidenceValue * 100} ${100 - confidenceValue * 100}`,
                                            }}
                                            transition={{
                                                duration: 1,
                                                delay: 0.2,
                                                ease: 'easeOut',
                                            }}
                                        />
                                        <defs>
                                            <linearGradient
                                                id="confidenceGradient"
                                                x1="0%"
                                                y1="0%"
                                                x2="100%"
                                                y2="100%"
                                            >
                                                <stop
                                                    offset="0%"
                                                    stopColor={
                                                        isVerified
                                                            ? confidenceValue >= 0.7
                                                                ? '#10b981'
                                                                : '#f59e0b'
                                                            : '#64748b'
                                                    }
                                                />
                                                <stop
                                                    offset="100%"
                                                    stopColor={
                                                        isVerified
                                                            ? confidenceValue >= 0.7
                                                                ? '#34d399'
                                                                : '#fbbf24'
                                                            : '#94a3b8'
                                                    }
                                                />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                    {isVerified ? (
                                        <CheckCircle2
                                            className={`absolute inset-0 m-auto w-4 h-4 ${confidenceColor}`}
                                        />
                                    ) : (
                                        <HelpCircle
                                            className={`absolute inset-0 m-auto w-4 h-4 ${confidenceColor}`}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Working State Strip */}
                {(data.intentName ||
                    data.verdict ||
                    data.reasonCode ||
                    data.nextActions?.length) && (
                    <div className="px-6 py-2 border-b border-slate-700/30 text-[11px] text-slate-300 flex flex-wrap gap-2">
                        {data.intentName && (
                            <span className="px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40">
                                Task: {data.intentName}
                            </span>
                        )}
                        {data.verdict && (
                            <span className="px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40">
                                Status:{' '}
                                {data.verdict === 'NOT_APPLICABLE' ? 'CHAT_ONLY' : data.verdict}
                            </span>
                        )}
                        {data.reasonCode && (
                            <span className="px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40">
                                Issue: {data.reasonCode}
                            </span>
                        )}
                        {data.nextActions && data.nextActions.length > 0 && (
                            <span className="px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40">
                                Next: {data.nextActions[0]?.label}
                            </span>
                        )}
                    </div>
                )}

                {/* Answer Content */}
                <div className="relative px-6 py-5">
                    <div className="relative">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2, duration: 0.5 }}
                            className={`text-[15px] leading-relaxed text-slate-100 overflow-hidden transition-all duration-300 ${
                                isLongContent && !isExpanded ? 'max-h-[200px]' : 'max-h-none'
                            } ${data.contentFormat === 'markdown' ? 'prose prose-invert prose-sm max-w-none' : 'whitespace-pre-wrap'}`}
                            style={
                                isLongContent && !isExpanded
                                    ? { maxHeight: `${COLLAPSED_MAX_HEIGHT}px` }
                                    : undefined
                            }
                        >
                            {data.contentFormat === 'markdown' ? (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {data.answer}
                                </ReactMarkdown>
                            ) : (
                                data.answer
                            )}
                        </motion.div>
                        {/* Gradient fade overlay when collapsed */}
                        {isLongContent && !isExpanded && (
                            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-900/95 via-slate-900/80 to-transparent pointer-events-none" />
                        )}
                    </div>
                    {/* Expand/Collapse toggle for long content */}
                    {isLongContent && (
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="flex items-center gap-1.5 mt-3 px-3 py-1.5 text-xs text-blue-400 hover:text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-lg transition-colors mx-auto"
                        >
                            {isExpanded ? (
                                <>
                                    <ChevronUp className="w-3.5 h-3.5" />
                                    <span>Show less</span>
                                </>
                            ) : (
                                <>
                                    <ChevronDown className="w-3.5 h-3.5" />
                                    <span>Show more</span>
                                </>
                            )}
                        </motion.button>
                    )}
                </div>

                {/* Action Bar */}
                <div className="relative px-6 py-3 border-t border-slate-700/30 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {/* Copy button */}
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={handleCopy}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg transition-colors"
                        >
                            {copied ? (
                                <>
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                    <span className="text-emerald-400">Copied</span>
                                </>
                            ) : (
                                <>
                                    <Copy className="w-3.5 h-3.5" />
                                    <span>Copy</span>
                                </>
                            )}
                        </motion.button>

                        {/* Share button */}
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg transition-colors"
                        >
                            <Share2 className="w-3.5 h-3.5" />
                            <span>Share</span>
                        </motion.button>
                    </div>

                    {/* Expand details toggle */}
                    {hasMetadata && showProvenance && (
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setShowDetails(!showDetails)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
                        >
                            <Brain className="w-3.5 h-3.5" />
                            <span>Provenance</span>
                            {showDetails ? (
                                <ChevronUp className="w-3.5 h-3.5" />
                            ) : (
                                <ChevronDown className="w-3.5 h-3.5" />
                            )}
                        </motion.button>
                    )}
                </div>

                {/* Expandable Details Panel */}
                <AnimatePresence>
                    {showDetails && hasMetadata && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="overflow-hidden"
                        >
                            <div className="px-6 py-4 bg-slate-900/50 border-t border-slate-700/30 space-y-4">
                                {/* Reasoning Summary */}
                                {data.reasoningSummary && data.reasoningSummary.length > 0 && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Zap className="w-3.5 h-3.5 text-violet-400" />
                                            <span className="text-xs font-medium text-slate-300">
                                                Reasoning Steps
                                            </span>
                                        </div>
                                        <div className="space-y-1.5 pl-5">
                                            {data.reasoningSummary.map((step, idx) => (
                                                <div
                                                    key={idx}
                                                    className="flex items-start gap-2 text-xs text-slate-400"
                                                >
                                                    <span className="text-slate-600 font-mono">
                                                        {idx + 1}.
                                                    </span>
                                                    <span>{step}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Sources */}
                                {data.sources && data.sources.length > 0 && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <BookOpen className="w-3.5 h-3.5 text-blue-400" />
                                            <span className="text-xs font-medium text-slate-300">
                                                Sources
                                            </span>
                                        </div>
                                        <div className="space-y-1.5 pl-5">
                                            {data.sources.map((source, idx) => (
                                                <div
                                                    key={idx}
                                                    className="flex items-center gap-2 text-xs"
                                                >
                                                    <span className="text-slate-400">
                                                        {source.title}
                                                    </span>
                                                    {source.relevance && (
                                                        <span className="text-slate-600">
                                                            ({Math.round(source.relevance * 100)}%
                                                            relevant)
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Artifacts */}
                                {artifactEntries.length > 0 && (
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Shield className="w-3.5 h-3.5 text-emerald-400" />
                                            <span className="text-xs font-medium text-slate-300">
                                                Artifacts
                                            </span>
                                        </div>
                                        <div className="space-y-1.5 pl-5 text-xs text-slate-400">
                                            {artifactEntries.map(([label, value]) => (
                                                <div key={label}>
                                                    {label}: {value as string}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Trace Info */}
                                {data.traceId && (
                                    <div className="flex items-center gap-4 text-[10px] text-slate-500 font-mono pt-2 border-t border-slate-700/30">
                                        <span>Trace: {data.traceId.slice(0, 16)}...</span>
                                        <button
                                            className="p-1 rounded hover:bg-slate-700/50 transition-colors"
                                            onClick={handleCopyTrace}
                                            title="Copy trace ID"
                                            type="button"
                                        >
                                            <Copy className="w-3 h-3" />
                                        </button>
                                        {data.model && <span>Model: {data.model}</span>}
                                        {data.responseTimeMs && (
                                            <span>{data.responseTimeMs}ms</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-violet-400/30 to-transparent" />
            </div>
        </motion.div>
    );
};

export default IntelligentResponseCard;
